const BGN_TO_EUR_RATE = 1.95583;
const DEFAULT_LOCALE = "bg-BG";

/**
 * Rounds a number to 2 decimal places using standard mathematical rounding
 * according to Bulgarian law (ЗВЕРБ чл. 13):
 * - If 3rd decimal < 5, 2nd decimal stays unchanged
 * - If 3rd decimal >= 5, 2nd decimal increases by 1
 */
function roundToTwoDecimals(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function createCurrencyFormatter(locale: string, currency: string, hasDecimals: boolean) {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  });
}

function processAmount(amount: number | string): number | null {
  if (typeof amount === 'string') {
    amount = Number(amount);
  }
  return isNaN(amount) ? null : amount;
}

function formatCurrency(amount: number | string, locale: string, currency: string): string {
  const processedAmount = processAmount(amount);

  if (processedAmount === null) {
    return '';
  }

  const hasDecimals = processedAmount % 1 !== 0;
  const formatter = createCurrencyFormatter(locale, currency, hasDecimals);

  return formatter.format(processedAmount);
}

function parseBGMoney(input: string): string | null {
  if (typeof input !== "string") return null;

  let s = input
    .replace(/\u00A0/g, " ")
    .replace(/лв\.?|лева|BGN|€/gi, "")
    .trim()
    .replace(/\s+/g, "");

  s = s.replace(/[^\d,.\-–]/g, "");

  if (!s) return null;
  return s;
}

function toNumberBG(s: string): number {
  if (s.includes(".") && s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
    return Number(s);
  }
  if (s.includes(",")) {
    s = s.replace(",", ".");
    return Number(s);
  }

  return Number(s);
}

function cleanEURString(input: string): string {
  return input.replace('€', '').replace('EUR', '').replace(/\D/g, '');
}

function isRangeString(input: string): boolean {
  return input.includes('-') || input.includes('–');
}

function parseRange(input: string): [number, number] | null {
  const parts = input.split(/[-–]/);
  if (parts.length !== 2) return null;
  const [min, max] = parts.map(Number);
  return (isNaN(min) || isNaN(max)) ? null : [min, max];
}

function convertAmount(amount: number, fromRate: number, toRate: number = 1): number {
  return (amount * toRate) / fromRate;
}

function convertBGNAmountToEUR(bgn: number): number {
  return convertAmount(bgn, BGN_TO_EUR_RATE);
}

function convertEURAmountToBGN(eur: number): number {
  return convertAmount(eur, 1, BGN_TO_EUR_RATE);
}

function convertAndFormatRange(
  rangeString: string,
  converter: (amount: number) => number,
  formatter: (amount: number) => string
): string {
  const range = parseRange(rangeString);
  if (!range) return '';

  const [min, max] = range;
  return `${formatter(converter(min))} - ${formatter(converter(max))}`;
}

function formatRange(
  rangeString: string,
  formatter: (amount: number) => string
): string {
  const range = parseRange(rangeString);
  if (!range) return '';

  const [min, max] = range;
  return `${formatter(min)} - ${formatter(max)}`;
}

function processStringInput(input: string): string | [number, number] | number {
  if (isRangeString(input)) {
    const cleaned = parseBGMoney(input);
    if (!cleaned) return "";
    const parts = cleaned.split(/[-–]/);
    if (parts.length !== 2) return "";
    const min = toNumberBG(parts[0]);
    const max = toNumberBG(parts[1]);
    return (isNaN(min) || isNaN(max)) ? "" : [min, max];
  }

  const cleaned = parseBGMoney(input);
  if (!cleaned) return "";
  const num = toNumberBG(cleaned);
  return isNaN(num) ? "" : num;
}

function processEURStringInput(input: string): string | [number, number] | number {
  if (isRangeString(input)) {
    const range = parseRange(input);
    return range || '';
  }

  const cleaned = cleanEURString(input);
  const num = Number(cleaned);
  return isNaN(num) ? '' : num;
}

function convertAndFormat(
  input: number | string,
  converter: (amount: number) => number,
  formatter: (amount: number) => string,
  stringProcessor?: (input: string) => string | [number, number] | number
): string {
  if (typeof input === 'string' && isRangeString(input)) {
    return convertAndFormatRange(input, converter, formatter);
  }

  if (typeof input === 'string' && stringProcessor) {
    const processed = stringProcessor(input);
    if (typeof processed === 'string') return processed; // Empty string case
    if (Array.isArray(processed)) {
      const [min, max] = processed;
      return `${formatter(converter(min))}-${formatter(converter(max))}`;
    }
    input = processed;
  }

  if (typeof input === 'number') {
    return formatter(converter(input));
  }

  const amount = processAmount(input);
  return amount !== null ? formatter(converter(amount)) : '';
}

function formatAndProcess(
  input: number | string,
  formatter: (amount: number) => string,
  stringProcessor?: (input: string) => string | [number, number] | number
): string {
  if (typeof input === 'string' && isRangeString(input)) {
    return formatRange(input, formatter);
  }

  if (typeof input === 'string' && stringProcessor) {
    const processed = stringProcessor(input);
    if (typeof processed === 'string') return processed; // Empty string case
    if (Array.isArray(processed)) {
      const [min, max] = processed;
      return `${formatter(min)}-${formatter(max)}`;
    }
    input = processed;
  }

  if (typeof input === 'number') {
    return formatter(input);
  }

  const amount = processAmount(input);
  return amount !== null ? formatter(amount) : '';
}

export function formatEUR(amount: number | string | undefined): string {
  if (!amount) {
    return "";
  }

  return formatAndProcess(
    amount,
    (num) => formatCurrency(num, DEFAULT_LOCALE, "EUR"),
    processEURStringInput
  );
}

export function formatBGN(amount: number | string | undefined): string {
  if (!amount) {
    return "";
  }

  return formatAndProcess(
    amount,
    (num) => formatCurrency(num, DEFAULT_LOCALE, "BGN"),
    processStringInput
  );
}

/**
 * Converts Bulgarian Lev (BGN) to Euro (EUR).
 */
export function convertBGNToEUR(bgn: number | string | undefined): string {
  if (!bgn) {
    return "";
  }

  return convertAndFormat(
    bgn,
    convertBGNAmountToEUR,
    (num) => formatCurrency(num, DEFAULT_LOCALE, "EUR"),
    processStringInput
  );
}

/**
 * Converts Euro (EUR) to Bulgarian Lev (BGN).
 */
export function convertEURToBGN(eur: number | string | undefined): string {
  if (!eur) {
    return "";
  }

  return convertAndFormat(
    eur,
    convertEURAmountToBGN,
    (num) => formatCurrency(num, DEFAULT_LOCALE, "BGN"),
    processEURStringInput
  );
}

/**
 * Converts and formats BGN to EUR with proper formatting.
 */
export function convertAndFormatBGNToEUR(bgn: number | string | undefined): string {
  if (!bgn) {
    return "";
  }

  return convertBGNToEUR(bgn);
}

/**
 * Formats price using EUR as primary source.
 * Shows EUR first, then BGN converted from EUR.
 */
export function formatPriceFromEUR(
  priceEur: number | string | undefined,
  priceRange: number | string | undefined
): { eur: string; eurRange?: string; bgn: string; bgnRange?: string } | null {
  const convertToNumber = (value: any): number | null => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
        return null;
      }
      const num = Number(trimmed);
      return isNaN(num) || num < 0 ? null : num;
    }
    if (typeof value === "number") {
      // Allow 0 as valid (free tests)
      if (isNaN(value) || value < 0) {
        return null;
      }
      return value;
    }
    return null;
  };

  const eurAmount = convertToNumber(priceEur);
  const eurRangeAmount = convertToNumber(priceRange);

  // Need at least priceEur to display anything
  if (eurAmount === null) {
    return null;
  }

  const eurFormatted = formatEUR(eurAmount);
  // Convert EUR to BGN and round to 2 decimal places according to ЗВЕРБ чл. 13
  const bgnAmount = roundToTwoDecimals(eurAmount * BGN_TO_EUR_RATE);
  const bgnFormatted = formatBGN(bgnAmount);

  const result: { eur: string; eurRange?: string; bgn: string; bgnRange?: string } = {
    eur: eurFormatted,
    bgn: bgnFormatted
  };

  // Add range if available
  if (eurRangeAmount !== null) {
    result.eurRange = formatEUR(eurRangeAmount);
    // Convert EUR range to BGN and round to 2 decimal places according to ЗВЕРБ чл. 13
    const bgnRangeAmount = roundToTwoDecimals(eurRangeAmount * BGN_TO_EUR_RATE);
    result.bgnRange = formatBGN(bgnRangeAmount);
  }

  return result;
}
